import { useCallback, useRef } from "react";
import { useEffect } from "react";
import { SubmitHandler, useForm, useFormState } from "react-hook-form";
import { useParams } from "react-router-dom";

import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  fetchCurrentUser,
  selectAuthErrorMessage,
  selectAuthIsFetching,
  selectCurrentUser,
  signupUser
} from "features/auth/auth-slice";

import PasswordField from "../../components/password-field";

interface IFormInput {
  email: string;
  password: string;
}

export default function Activate() {
  const loginButtonRef = useRef<HTMLButtonElement>(null);
  const dispatch = useAppDispatch();
  const { control, handleSubmit } = useForm<IFormInput>({
    mode: "onBlur",
    defaultValues: { email: "", password: "" }
  });
  const { isValid } = useFormState({
    control
  });
  const { uid, token } = useParams();
  useEffect(() => {
    if (uid && token && dispatch) {
      dispatch(fetchCurrentUser({ uid, token }));
    }
  }, [token, uid, dispatch]);
  const isFetching = useAppSelector(selectAuthIsFetching);
  const errorMessage = useAppSelector(selectAuthErrorMessage);
  const {
    first_name: firstName,
    last_name: lastName,
    email
  } = useAppSelector(selectCurrentUser);
  const onSubmit: SubmitHandler<IFormInput> = ({ password }) => {
    const signupData = {
      password,
      uid,
      token
    };
    dispatch(signupUser(signupData));
  };
  const handleOnBlur = useCallback(() => {
    if (loginButtonRef.current) loginButtonRef.current.focus();
  }, []);
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={4}>
        {Boolean(errorMessage) && (
          <Alert icon={false} severity="error">
            {errorMessage}
          </Alert>
        )}
        <Box>
          <Typography variant="h3" gutterBottom component="div">
            Get Started
          </Typography>
          <Typography variant="subtitle1" fontSize={18} component="div">
            Welcome to Suno - Let's activate your account
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              variant="filled"
              disabled
              fullWidth
              label="First Name"
              value={firstName}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              variant="filled"
              disabled
              fullWidth
              label="Last Name"
              value={lastName}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant="filled"
              disabled
              fullWidth
              label="Email Address"
              value={email}
            />
          </Grid>
          <Grid item xs={12}>
            <PasswordField
              name="password"
              label="Password"
              control={control}
              onBlur={handleOnBlur}
              validatePassword
            />
          </Grid>
          <Grid item container xs={12} justifyContent="center">
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isFetching}
              disabled={!isValid}
              sx={{ mt: 2 }}
            >
              Activate Account
            </LoadingButton>
          </Grid>
        </Grid>
      </Stack>
    </form>
  );
}
